/**
 * ╔═══════════════════════════════════════════════════════════════════════╗
 * ║  SINGLE OWNER: Job Address Resolution                                ║
 * ║                                                                       ║
 * ║  BUSINESS RULE:                                                       ║
 * ║  If a user typed over the collection address on a job, that typed     ║
 * ║  address is the source of truth. The customer master address is       ║
 * ║  ONLY used as a fallback when the job-level address is blank/null.    ║
 * ║                                                                       ║
 * ║  Delivery address is ALWAYS stored directly on the job record.        ║
 * ║  There is no customer fallback for delivery.                          ║
 * ║                                                                       ║
 * ║  This module is the SINGLE OWNER of job address resolution.           ║
 * ║  Every UI, PDF, navigation, and invoice MUST use these helpers.       ║
 * ║                                                                       ║
 * ║  NEVER resolve job addresses inline — always call these functions.    ║
 * ║  NEVER use job.customers?.address without checking job-level first.   ║
 * ╚═══════════════════════════════════════════════════════════════════════╝
 */

/**
 * Minimal shape a job object must have for address resolution.
 * Accepts any superset (full job row, partial select, etc).
 */
export interface JobAddressLike {
  collection_address?: string | null;
  collection_postcode?: string | null;
  delivery_address?: string | null;
  delivery_postcode?: string | null;
  customers?: {
    address?: string | null;
    postcode?: string | null;
  } | null;
}

// ---------------------------------------------------------------------------
// Collection — job-level first, customer fallback
// ---------------------------------------------------------------------------

/**
 * Resolves the collection address for a job.
 *
 * Priority: job.collection_address > job.customers.address > ""
 *
 * @critical Used by: dashboard cards, navigation, PDF, invoices, job details
 */
export function resolveCollectionAddress(job: JobAddressLike | null | undefined): string {
  if (!job) return "";
  return (job as any).collection_address || job.customers?.address || "";
}

/**
 * Resolves the collection postcode for a job.
 *
 * Priority: job.collection_postcode > job.customers.postcode > ""
 */
export function resolveCollectionPostcode(job: JobAddressLike | null | undefined): string {
  if (!job) return "";
  return (job as any).collection_postcode || job.customers?.postcode || "";
}

// ---------------------------------------------------------------------------
// Delivery — always from job record, no customer fallback
// ---------------------------------------------------------------------------

/**
 * Resolves the delivery address for a job.
 * Delivery address is stored directly on the job — no customer fallback.
 */
export function resolveDeliveryAddress(job: JobAddressLike | null | undefined): string {
  if (!job) return "";
  return job.delivery_address || "";
}

/**
 * Resolves the delivery postcode for a job.
 */
export function resolveDeliveryPostcode(job: JobAddressLike | null | undefined): string {
  if (!job) return "";
  return job.delivery_postcode || "";
}

// ---------------------------------------------------------------------------
// Legacy aliases — kept for backward compatibility
// ---------------------------------------------------------------------------

/** @deprecated Use resolveCollectionAddress instead */
export const getJobCollectionAddress = resolveCollectionAddress;
/** @deprecated Use resolveCollectionPostcode instead */
export const getJobCollectionPostcode = resolveCollectionPostcode;
/** @deprecated Use resolveDeliveryAddress instead */
export const getJobDeliveryAddress = resolveDeliveryAddress;
/** @deprecated Use resolveDeliveryPostcode instead */
export const getJobDeliveryPostcode = resolveDeliveryPostcode;
